import styled from "@emotion/styled";
import { Link, useNavigate } from "react-router";
import type { RouteID } from "../../routing/routes";
import AwardBadge from "./img/iib-badge-2019.png";
import {
  borderRadius,
  breakpoints,
  fontFamily,
  fontWeight,
  fontSize,
  zIndex,
  border as borderToken,
  status,
  lineHeight,
  text,
} from "@atlas-int/tokens";

const CardContainer = styled.div`
  background: rgba(36, 55, 68, 0.04);
  box-shadow: 0px 0px 4px 2px rgba(0, 0, 0, 0.25);
  border-radius: ${borderRadius.base};
  display: flex;
  flex-direction: column;
  cursor: pointer;
  overflow: hidden;
  padding: 1.5rem;
  box-sizing: border-box;
  flex: 0 1 calc(50% - 2.5rem);
  min-width: 360px;
  max-width: 560px;
  container-type: inline-size;

  @media (max-width: ${breakpoints.lg}) {
    flex-basis: calc(50% - 1.5rem);
  }

  @media (max-width: ${breakpoints.sm}) {
    flex-basis: 100%;
    width: 100%;
    min-width: unset;
    max-width: 500px;
  }
`;

const Title = styled.h2`
  font-family: ${fontFamily.base}, sans-serif;
  font-size: clamp(16px, 5cqw, 26px);
  font-weight: ${fontWeight.semibold};
  color: ${text.primary};
  text-transform: uppercase;
  margin: 0 0 1rem 0;
  white-space: nowrap;
`;

// Card images are all 3024×1889 (≈1.6:1). Setting `aspect-ratio` on the
// container reserves the slot before the image loads so the page below the
// card doesn't jump when the file arrives. `max-height: 240px` still caps
// tall cards on wide layouts; on narrower cards the aspect-ratio drives
// the height instead.
const CARD_IMAGE_WIDTH = 3024;
const CARD_IMAGE_HEIGHT = 1889;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 1rem;
  aspect-ratio: ${CARD_IMAGE_WIDTH} / ${CARD_IMAGE_HEIGHT};
  max-height: 240px;
  overflow: hidden;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
  display: block;
  border: 1px solid ${borderToken.subtle};
`;

const BottomRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.5rem;
  margin-top: auto;
`;

const Body = styled.p`
  margin: 0;
  font-family: ${fontFamily.base}, sans-serif;
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.normal};
  color: ${text.primary};
  line-height: ${lineHeight.snug};
  flex: 1;
  max-width: 336px;
`;

const ctaColor = status.errorDark;

const CTAContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const CTALink = styled(Link)`
  color: ${ctaColor};
  text-decoration: none;
  font-family: ${fontFamily.base}, sans-serif;
  font-weight: ${fontWeight.semibold};
  font-size: ${fontSize.lg};
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const CTAArrow = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5 12H19M19 12L13 6M19 12L13 18"
      stroke={ctaColor}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AwardBadgeContainer = styled.a`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: ${zIndex.base};
`;

const Award = styled.img`
  width: 80px;
`;

interface Props {
  title: string;
  body: string;
  ctaText: string;
  ctaDestination: RouteID;
  imageUrl: string;
  showAward: boolean;
  href: string;
  analyticsEvent: () => void;
}

const Card = (props: Props) => {
  const { title, body, ctaText, imageUrl, showAward, href, analyticsEvent } =
    props;

  const navigate = useNavigate();

  const awardIcon =
    showAward === false ? null : (
      <AwardBadgeContainer
        href="https://www.informationisbeautifulawards.com/"
        target="_blank"
        onClick={(e) => e.stopPropagation()}
      >
        <Award src={AwardBadge} />
      </AwardBadgeContainer>
    );

  const onClick = () => {
    navigate(href);
  };

  const onCTAClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    analyticsEvent();
    navigate(href);
  };

  return (
    <CardContainer onClick={onClick}>
      <Link
        to={href}
        style={{ textDecoration: "none" }}
        onClick={(e) => e.preventDefault()}
      >
        <Title>{title}</Title>
      </Link>
      <ImageContainer>
        <Image
          src={imageUrl}
          alt={title}
          width={CARD_IMAGE_WIDTH}
          height={CARD_IMAGE_HEIGHT}
          decoding="async"
        />
        {awardIcon}
      </ImageContainer>
      <BottomRow>
        <Body>{body}</Body>
        <CTAContainer>
          <CTALink to={href} onClick={onCTAClick}>
            {ctaText}
          </CTALink>
          <CTAArrow />
        </CTAContainer>
      </BottomRow>
    </CardContainer>
  );
};

export default Card;
